<script setup lang="ts">
import { computed } from "vue";
import Button from "@/components/general/Button/Button.vue";

interface Props {
    id: string;
    inputName: string;
    min?: number;
    max?: number;
    ariaLabelledBy?: string;
    ariaDescribedBy?: string;
    ariaErrorMessage?: string;
    ariaValueText?: string;
    disabled?: boolean;
    invalid?: boolean;
}

const {
    id,
    min = 0,
    max = 100,
    ariaLabelledBy = "",
    ariaDescribedBy = "",
    ariaErrorMessage = "",
    ariaValueText = "",
    disabled,
    invalid,
} = defineProps<Props>();

const emit = defineEmits(["change"]);
const model = defineModel<number>();

const rangeModel = computed({
    get() {
        return model.value ?? 0;
    },
    set(value) {
        model.value = Number(value);
    },
});

const increase = () => {
    if (rangeModel.value + 1 <= max) {
        rangeModel.value += 1;
    }
    emit("change", "increase");
};

const decrease = () => {
    if (rangeModel.value - 1 >= min) {
        rangeModel.value -= 1;
    }
    emit("change", "decrease");
};

const sliderChange = () => {
    emit("change", "slider");
};
</script>

<template>
    <div>
        <div
            :class="{
                'range-field': true,
                invalid,
                disabled,
            }"
        >
            <Button
                :id="`${id}-decrease`"
                :disabled="rangeModel <= min"
                aria-label="minus"
                class="control minus"
                icon="minus"
                btn-type="tertiary"
                @click.prevent="decrease()"
            />
            <div class="mobile-content">
                <slot />
            </div>
            <input
                :id="`${id}-slider`"
                v-model="rangeModel"
                :max="max"
                :min="min"
                :name="inputName"
                :aria-describedby="ariaDescribedBy"
                :aria-disabled="disabled"
                :aria-errormessage="ariaErrorMessage"
                :aria-invalid="invalid"
                :aria-labelledby="ariaLabelledBy"
                :aria-valuetext="ariaValueText"
                :disabled="disabled"
                class="range"
                type="range"
                @change="sliderChange"
            />
            <Button
                :id="`${id}-increase`"
                :disabled="rangeModel >= max"
                class="control plus"
                icon="plus"
                aria-label="plus"
                btn-type="tertiary"
                @click.prevent="increase()"
            />
        </div>
    </div>
</template>

<style scoped>
.range-field {
    display: grid;
    align-items: flex-end;
    gap: 0;
    padding: 1rem 2rem;
    width: 100%;
    max-width: 320px;
    background: var(--color-primary-mid);
    cursor: pointer;
    justify-content: stretch;
    grid-template-columns:
        var(--input-size-range-control)
        auto
        var(--input-size-range-control);
}

.control {
    width: var(--input-size-range-control);
    height: var(--input-size-range-control);
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    color: var(--color-primary-mid);
    padding: 0;
    position: relative;
    bottom: 0.5rem;

    &.minus {
        right: -1rem;
        justify-self: flex-end;
    }

    &.plus {
        left: -1rem;
        justify-self: flex-start;
    }
}

.control,
.control:disabled,
.control:focus,
.control:active,
.control:hover {
    background: var(--pure-white);
}

.mobile-content {
    display: block;
}

.range {
    all: unset;
    display: none;
    appearance: none;
    width: 100%;
    height: var(--input-size-range-thumb);

    &:focus {
        outline: var(--border-width-range-thumb) solid
            var(--color-element-focus-outline);
        outline-offset: var(--border-width-range-thumb);
    }

    &::-webkit-slider-runnable-track {
        background: var(--range-track-background);
        height: var(--input-height-range-track);
        border-radius: var(--border-radius-range-track);
    }

    &::-moz-range-track {
        background: var(--range-track-background);
        height: var(--input-height-range-track);
        border-radius: var(--border-radius-range-track);
    }

    &::-ms-track {
        background: var(--range-track-background);
        height: var(--input-height-range-track);
        border-radius: var(--border-radius-range-track);
    }

    &::-webkit-slider-thumb {
        appearance: none;
        box-sizing: border-box;
        box-shadow: none;
        width: var(--input-size-range-thumb);
        height: var(--input-size-range-thumb);
        border: var(--border-width-range-thumb) solid
            var(--color-range-thumb-shadow);
        border-radius: 50%;
        background: var(--color-range-thumb);
        margin-top: calc(
            calc(
                (var(--input-height-range-track) / 2) -
                    calc(var(--input-size-range-thumb) / 2)
            )
        );
    }

    &::-moz-range-thumb {
        appearance: none;
        box-sizing: border-box;
        box-shadow: none;
        width: var(--input-size-range-thumb);
        height: var(--input-size-range-thumb);
        border: var(--border-width-range-thumb) solid
            var(--color-range-thumb-shadow);
        border-radius: 50%;
        background: var(--color-range-thumb);
    }

    &::-ms-thumb {
        appearance: none;
        box-sizing: border-box;
        box-shadow: none;
        width: var(--input-size-range-thumb);
        height: var(--input-size-range-thumb);
        border: var(--border-width-range-thumb) solid
            var(--color-range-thumb-shadow);
        border-radius: 50%;
        background: var(--color-range-thumb);
    }

    &:focus::-moz-range-thumb {
        box-shadow:
            0 0 0 1px #fff,
            0 0 0 0.2rem rgb(216 80 58 / 25%);
    }

    &:focus::-webkit-slider-thumb {
        box-shadow:
            0 0 0 1px #fff,
            0 0 0 0.2rem rgb(216 80 58 / 25%);
    }

    &:focus::-ms-thumb {
        box-shadow:
            0 0 0 1px #fff,
            0 0 0 0.2rem rgb(216 80 58 / 25%);
    }
}

@media screen and (width >= 768px) {
    .mobile-content {
        display: none;
    }

    .range {
        display: block;
    }

    .range-field {
        background-color: transparent;
        max-width: 100%;
        gap: 0.5rem;
        align-items: center;
        padding: 0;
    }

    .control,
    .control:disabled,
    .control:focus,
    .control:active,
    .control:hover {
        background: var(--color-primary-dark);
        color: var(--pure-white);
        position: static;
    }
}

@media screen and (width <= 575px) {
    .range-field {
        max-width: 100%;
        padding: 1rem 0.5rem;
        grid-template-columns: auto 200px auto;
    }
}
</style>
